// GLOBAL mixin for all Vue components
// for working with i18n things

import { splI18nDataWindowVarName, splLocaleWindowVarName, coreTranslationsWindowVarName, defaultLocaleObject } from './constants';
import { normalizeLocale } from './utils';  // utils for working with localization / i18n machinery

/**
 * This is a GLOBAL mixin added to all Vue components, so it can be used anywhere in the app.
 * It provides some computed properties and methods for working with localization / i18n.
 * 
 * Computed properties:
 * - $splLocaleInfo: returns an object with the current locale info (window locale, lib locale, lang-only locale)
 * - $splLocale: returns the current locale code (NOT NORMALIZED) -- so it will be something like 'en-US' or 'es' or 'es-MX'
 * - $coreTranslations: returns the core translations from our context processor (which is stashed on the window by the template)
 * 
 * Methods:
 * - $splTrans(key): returns the translation for the given key from the core translations (or the key itself if not found)
 * 
 * Usage:
 * import globalSplI18nMixin from '@/localization/globalMixin';
 * 
 * export default {
 *     mixins: [globalSplI18nMixin],
 *     ...
 * }
 */
const globalSplI18nMixin = {
    computed: {
        $splLocaleInfo() {
            // combination of many "lower-level" locale things, mostly used internally here
            // so we nest it all together to avoid polluting the global component namespace with too many things
            const windowLocale = window[splLocaleWindowVarName];
            const libLocale = this.$i18n?.locale;
            return {
                windowLocale: windowLocale,  // not setting defaults here, so we know if it was unset and we can handle it accordingly
                libLocale: libLocale,  // not setting defaults here, so we know if it was unset and we can handle it accordingly
                // add more stuff that you get from backend i18n object
            };
        },
        $langInfo() {
            // return the current language info from the i18nData object (which is stashed on the window by the template)
            // this matches our LangInfo object/dict/dataclass on backend:  { code, name, name_local, name_translated, bidi }
            return this.$i18nData.lang_info || {};
        },
        $i18nData() {
            // from our i18nData object (which is stashed on the window by the template)
            return window[splI18nDataWindowVarName] || {};
        },
        $splLocale() {
            // return the current locale code (NORMALIZED BY PASSING THROUGH Intl.Locale().baseName)
            // so it will be something like 'en-US' or 'es' or 'es-MX' -- SHOULD IT BE NORMALIZED???
            // this should work even if the i18n instance is not yet created or not on the page -- so things like ag-grid can still get the current locale
            const rawLocale = this.$splLocaleInfo?.libLocale || this.$splLocaleInfo?.windowLocale || defaultLocaleObject.baseName;
            return normalizeLocale(rawLocale);  // returns the .baseName of the locale (e.g., 'en', 'es', 'es-MX', 'zh-Hans-CN', 'fr', etc.)
        },
        $coreTranslations() {
            // get core translations from our context processor (which is stashed on the window by the template)
            return window[coreTranslationsWindowVarName] || {};
        },
        $localeIsCJK() {
            // return true if the current locale is a CJK (Chinese, Japanese, Korean) locale
            // used to adapt the UI for CJK locales (e.g., font size, line height, spacing, etc.)
            const cjkLocales = ['zh', 'zh-Hans', 'zh-Hant', 'ja', 'ko'];
            return this.$splLocale ? cjkLocales.some(cjkLocale => this.$splLocale.startsWith(cjkLocale)) : false;
        },
    },
    methods: {
        $splTrans(key) {
            // get core translations from our context processor (which is stashed on the window by the template)
            const translations = this.$coreTranslations || {};
            // TODO - get from various places... pageTranslations? (page-specific), coreTranslations, jsCatalog?, etc...
            return translations[key] || key;  // fallback to key if not found
        },
    }
};

export default globalSplI18nMixin;